import { useEffect, type Dispatch, type MutableRefObject, type SetStateAction } from "react";
import type { Socket } from "socket.io-client";
import type { KanbanList, KanbanTag } from "../../../types/kanban";
import type { Msg } from "../../../types/chat";
import type { useChannelLocks } from "../../../hooks/useChannelLocks";
import { registerChatSocketEvents } from "../../socket/useChatSocketEvents";
import { applyFormAnswer, applyFormState, formChannelKey } from "../formState";
import type { Channel, FormQuestion } from "../chatTypes";
import type { useAccountSocketHandlers } from "./useAccountSocketHandlers";
import type { useChannelOrder } from "./useChannelOrder";
import type { useFormData } from "./useFormData";
import type { useIncomingMessageHandlers } from "./useIncomingMessageHandlers";
import type { useKanbanData } from "./useKanbanData";
import type { useMessageSocketHandlers } from "./useMessageSocketHandlers";

const k = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);
const fq = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);
const normalizeChannelId = (chan: string) => (chan.includes(':') ? chan.split(':').slice(-1)[0] : chan);

/**
 * Re-registered on each channel/space switch so handler closures see the current
 * channel/void. registerChatSocketEvents just socket.off's then socket.on's, which
 * is cheap — no connect()/disconnect() and no onConnect side-effects happen here.
 * Registers the chat socket event handlers. ChatLayout owns the handler groups (account, message,
 * incoming); this adds the channel-list / kanban / form handlers and wires everything up.
 */
export function useChatSocketRegistration({
  socket,
  selfUserId,
  currentChannelId,
  currentVoidIdRef,
  accountSocketHandlers,
  messageSocketHandlers,
  incomingMessageHandlers,
  channelTypeById,
  setChannels,
  setLockedChannelMap,
  setChannelTypeById,
  setChanOrder,
  setMsgsByKey,
  pendingChannelSwitchRef,
  switchChannel,
  loadKanbanIfNeeded,
  loadFormIfNeeded,
  setKanbanByChan,
  setChannelTagsByChan,
  setFormByChan,
  scheduleFormReload,
}: {
  socket: Socket;
  selfUserId?: string;
  currentChannelId: string;
  currentVoidIdRef: MutableRefObject<string>;
  accountSocketHandlers: ReturnType<typeof useAccountSocketHandlers>;
  messageSocketHandlers: ReturnType<typeof useMessageSocketHandlers>;
  incomingMessageHandlers: ReturnType<typeof useIncomingMessageHandlers>;
  channelTypeById: ReturnType<typeof useChannelOrder>["channelTypeById"];
  setChannels: Dispatch<SetStateAction<Channel[]>>;
  setLockedChannelMap: ReturnType<typeof useChannelLocks>["setLockedChannelMap"];
  setChannelTypeById: ReturnType<typeof useChannelOrder>["setChannelTypeById"];
  setChanOrder: ReturnType<typeof useChannelOrder>["setChanOrder"];
  setMsgsByKey: Dispatch<SetStateAction<Record<string, Msg[]>>>;
  pendingChannelSwitchRef: ReturnType<typeof useChannelLocks>["pendingChannelSwitchRef"];
  switchChannel: (id: string) => void;
  loadKanbanIfNeeded: ReturnType<typeof useKanbanData>["loadKanbanIfNeeded"];
  loadFormIfNeeded: ReturnType<typeof useFormData>["loadFormIfNeeded"];
  setKanbanByChan: ReturnType<typeof useKanbanData>["setKanbanByChan"];
  setChannelTagsByChan: ReturnType<typeof useKanbanData>["setChannelTagsByChan"];
  setFormByChan: ReturnType<typeof useFormData>["setFormByChan"];
  scheduleFormReload: ReturnType<typeof useFormData>["scheduleFormReload"];
}) {
  useEffect(() => {
    // The small user/alert handlers come from useAccountSocketHandlers (called above this effect).
    const { onAuthAccepted, onChannelsReordered, onNotificationClear, onUnreadReset, onJoinRequestsChanged, onSpaceNotify, onUserStatus } = accountSocketHandlers;

    const onChannelList = ({ voidId, channels }: { voidId: string; channels: Channel[] }) => {
      if (voidId !== currentVoidIdRef.current) return;
      const normalized = channels.map((c) => {
        const fid = fq(voidId, c.id);
        const rawType = (c.type || channelTypeById[fid] || "").toString().trim().toLowerCase();
        const nameHint = (c.name || "").toString().toLowerCase();
        const inferredFromName = nameHint.includes("calendar") ? "calendar" : "";
        const patchedType =
          (rawType && rawType !== "text") ? rawType :
          inferredFromName || rawType || "text";
        return {
          ...c,
          type: patchedType,
          locked: Boolean(c.locked),
          folderId: c.folderId || null,
          position: typeof c.position === 'number' ? c.position : Number(c.position || 0),
          allowedRoles: Array.isArray((c as any).allowedRoles)
            ? (c as any).allowedRoles
            : Array.isArray((c as any).allowed_roles)
              ? (c as any).allowed_roles
              : [],
        };
      });
      const sorted = [...normalized].sort((a, b) => {
        const posDiff = Number(b.position || 0) - Number(a.position || 0);
        return posDiff !== 0 ? posDiff : a.name.localeCompare(b.name);
      });
      setChannels(sorted);
      setLockedChannelMap((prev) => {
        const next = { ...prev };
        for (const c of normalized) {
          const key = fq(voidId, c.id);
          if (c.locked) next[key] = true;
          else if (next[key]) delete next[key];
        }
        return next;
      });
      // Persist channel types for favorites rendering outside current space
      try {
        setChannelTypeById(prev => {
          const next = { ...prev } as Record<string,string>;
          for (const c of normalized) { const fid = fq(voidId, c.id); if (c.type) next[fid] = String(c.type); }
          return next;
        });
      } catch { /* noop */ }
      setChanOrder((prev) => ({ ...prev, [voidId]: sorted.map((c) => c.id) }));
      setMsgsByKey((old) => {
        const next = { ...old };
        for (const c of channels) {
          const kk = k(voidId, c.id);
          if (!next[kk]) next[kk] = [];
        }
        return next;
      });
      try {
        const raw = localStorage.getItem('__echo_channels_cache__');
        const cache = raw ? JSON.parse(raw) : {};
        cache[voidId] = channels.map((c) => ({ id: c.id, name: c.name, type: c.type || 'text' }));
        localStorage.setItem('__echo_channels_cache__', JSON.stringify(cache));
      } catch {
        /* noop */
      }
      const pendingSwitch = pendingChannelSwitchRef.current;
      let appliedPending = false;
      if (pendingSwitch && pendingSwitch.voidId === voidId) {
        // channels in `sorted` carry fully-qualified ids, so compare against the fq
        // form — comparing a short id here always missed and dropped us onto the
        // first channel instead of the one we asked for.
        const desiredFq = fq(voidId, normalizeChannelId(pendingSwitch.channelId));
        const exists = sorted.find(c => c.id === desiredFq);
        if (exists) {
          pendingChannelSwitchRef.current = null;
          appliedPending = true;
          if (currentChannelId !== desiredFq) {
            switchChannel(desiredFq);
          }
        } else {
          pendingChannelSwitchRef.current = null;
        }
      }
      if (!appliedPending && !sorted.find(c => c.id === currentChannelId) && sorted[0]) {
        switchChannel(sorted[0].id);
      }
      // Ensure special channel data is loaded after list arrives (avoids race on initial connect)
      try {
        const curMeta = channels.find(c => c.id === currentChannelId);
    if (curMeta) {
      const fqid = fq(voidId, currentChannelId);
      if (curMeta.type === 'kanban') loadKanbanIfNeeded(fqid);
      if (curMeta.type === 'form') loadFormIfNeeded(fqid);
    }
  } catch { /* noop */ }
};

    function onKanbanState({ channelId, lists, tags }: { channelId: string; lists?: KanbanList[]; tags?: KanbanTag[] }) {
      if (Array.isArray(lists)) {
        setKanbanByChan(old => ({ ...old, [channelId]: lists }));
      }
      if (Array.isArray(tags)) {
        setChannelTagsByChan(old => ({ ...old, [channelId]: tags }));
      }
    }
    function onFormState({ channelId, questions, allSubmitted, participants }: { channelId: string; questions: FormQuestion[]; allSubmitted?: Record<string, boolean>; participants?: string[] }) {
      const cid = formChannelKey(channelId, currentVoidIdRef.current);
      setFormByChan(old => applyFormState(old, cid, { questions, allSubmitted, participants }));
    }

    // onSeen / onEdited / onDeleted / onReactions / onBacklog come from useMessageSocketHandlers (called above this effect).
    const { onSeen, onEdited, onDeleted, onReactions, onBacklog } = messageSocketHandlers;
    // onNew / onMessageAck come from useIncomingMessageHandlers (called above this effect).
    const { onNew, onMessageAck } = incomingMessageHandlers;

    // Receive live form answer updates
    const onFormAnswer = ({ channelId, questionId, userId, answer, hasAnswer }: { channelId: string; questionId: string; userId: string; answer?: string; hasAnswer?: boolean }) => {
      const cid = formChannelKey(channelId, currentVoidIdRef.current);
      setFormByChan(old => applyFormAnswer(old, cid, { questionId, userId, answer, hasAnswer }, selfUserId));
      // Ensure we’re fully in sync with server (covers cases where server omits some users)
      scheduleFormReload(cid, 200);
    };
      // Connection lifecycle (connect/disconnect/connect_error) is owned by the
      // stable effect above — pass those as undefined so they aren't re-registered
      // (and onConnect's side-effects don't re-fire) on every channel switch.
      const unregister = registerChatSocketEvents(socket, {
        onAuthAccepted,
        onChannelList,
        onChannelBacklog: onBacklog,
        onKanbanState,
        onFormState,
        onNewMessage: onNew,
        onMessageAck,
        onSeen,
        onEdited,
        onDeleted,
        onReactions,
        onChannelsReordered,
        onJoinRequestsChanged,
      onFormAnswer,
      onUserStatus,
      onSpaceNotify,
      onNotificationClear,
      onUnreadReset,
      });

    return () => {
      unregister();
    };
    // No dependency array on purpose: these handlers read many pieces of current state (channels,
    // favorites, hidden spaces, editing id, ...), so they are re-registered after every render to always
    // see it. Re-registering is cheap (socket.off/on of this component's own handler references only).
  });
}
